import { DateValue, parseDate } from '@internationalized/date';
import { useDateRangePicker } from '@react-aria/datepicker';
import { useDateRangePickerState } from '@react-stately/datepicker';
import { AriaDateRangePickerProps } from '@react-types/datepicker';
import classNames from 'classnames';
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  endOfDay,
  isEqual,
  setHours,
  setMinutes,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import _ from 'lodash';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  toDateFromLocaleStringDate,
  toLocaleDateString,
  tz,
} from '../../../common/dateTimeHelper';
import * as Icon from '../../../components/icons';
import { Button } from '../../actions/Button';
import Dialog from '../Dialog';
import Popover from '../Popover';
import InputDateField from './InputDateField';
import RangeCalendar from './RangeCalendar';

type CustomDuration = {
  title: string;
  start: Date;
  end: Date;
};

// Add shift data types
type ShiftPeriod = {
  start: Date;
  end: Date;
  shiftId?: string;
  employeeId?: string;
};

// New: Shift type configuration
type ShiftTypeConfig = {
  id: string;
  name: string;
  startHour: number; // 24-hour format (0-23)
  startMinute?: number; // Minutes (0-59), defaults to 0
  endHour: number; // Can be > 23 for next day (e.g., 28 for 4:00 next day)
  endMinute?: number; // Minutes (0-59), defaults to 0
  color?: string;
};

type ShiftSelectorConfig = {
  shifts?: ShiftPeriod[];
  maxShiftsToShow?: number;
  enableShiftSelectors?: boolean;
  shiftTypes?: ShiftTypeConfig[]; // New: Configure shift types
  enableShiftTypeSelectors?: boolean; // New: Enable shift type-based selectors
};

// Function to categorize a shift by its start time
const categorizeShiftByTime = (
  shift: ShiftPeriod,
  shiftTypes: ShiftTypeConfig[],
): ShiftTypeConfig | null => {
  const startHour = shift.start.getHours();
  const startMinutes = shift.start.getMinutes();
  const startTimeDecimal = startHour + startMinutes / 60;

  for (const shiftType of shiftTypes) {
    let {
      startHour: typeStart,
      endHour: typeEnd,
      startMinute = 0,
      endMinute = 0,
    } = shiftType;

    // Convert to decimal hours for comparison
    const typeStartDecimal = typeStart + startMinute / 60;
    let typeEndDecimal = typeEnd + endMinute / 60;

    // Handle overnight shifts (e.g., 22:30 to 4:15 next day)
    if (typeEndDecimal <= typeStartDecimal) {
      typeEndDecimal += 24;
    }

    // Check if shift start time falls within this shift type range
    let isMatch = false;
    if (typeEndDecimal <= 24) {
      // Same day shift
      isMatch =
        startTimeDecimal >= typeStartDecimal &&
        startTimeDecimal < typeEndDecimal;
    } else {
      // Overnight shift - check both parts
      isMatch =
        startTimeDecimal >= typeStartDecimal || // After midnight start
        startTimeDecimal < typeEndDecimal - 24; // Before midnight end
    }

    if (isMatch) {
      return shiftType;
    }
  }

  return null;
};

// Default shift type configurations
const getDefaultShiftTypes = (): ShiftTypeConfig[] => [];

export type DateRangePickerProps = Omit<
  AriaDateRangePickerProps<DateValue>,
  'value' | 'onChange' | 'minValue' | 'maxValue'
> & {
  value: [Date, Date];
  minValue?: Date;
  maxValue?: Date;
  onChange?: (range: [Date, Date]) => void;
  onResetClick?: () => void;
  className?: string;
  classNameInput?: string;
  showTimeMenu?: boolean;
  showDateMenu?: boolean;
  headerMode?: boolean;
  selectorPosition?: 'start' | 'end';
  shiftConfig?: ShiftSelectorConfig;
};

const DateRangePicker: React.FC<DateRangePickerProps> = props => {
  const { t } = useTranslation('app');

  const pickerProps: AriaDateRangePickerProps<DateValue> = {
    ...props,
    'aria-label': props['aria-label'] ?? t`Select date range`,
    value: _.isEmpty(props.value)
      ? null
      : {
          start: parseDate(toLocaleDateString(props.value?.[0])),
          end: parseDate(toLocaleDateString(props.value?.[1])),
        },
    minValue: props.minValue
      ? parseDate(toLocaleDateString(props.minValue))
      : undefined,
    maxValue: props.maxValue
      ? parseDate(toLocaleDateString(props.maxValue))
      : undefined,
    onChange: range => {
      setTimeout(() => {
        props.onChange([
          startOfDay(range.start.toDate(tz)),
          endOfDay(range.end.toDate(tz)),
        ]);
      }, 0);
    },
  };
  const state = useDateRangePickerState(pickerProps);
  const ref = useRef();
  const {
    groupProps,
    labelProps,
    startFieldProps,
    endFieldProps,
    buttonProps,
    dialogProps,
    calendarProps,
  } = useDateRangePicker(pickerProps, state, ref);

  function selectDuration(start: Date, end: Date) {
    // Find which duration was selected
    const selectedDuration = customDurations.find(
      d => isEqual(d.start, start) && isEqual(d.end, end),
    );

    console.log('🎯 Time range selected:', {
      selectorTitle: selectedDuration?.title || 'Unknown',
      requestedRange: { start, end },
      startTime: `${start.getHours()}:${String(start.getMinutes()).padStart(2, '0')}`,
      endTime: `${end.getHours()}:${String(end.getMinutes()).padStart(2, '0')}`,
      isShiftTypeSelector:
        (selectedDuration?.title.includes('Last ') &&
          selectedDuration?.title.includes('(')) ||
        false,
    });

    state.close();

    let actualStart: Date, actualEnd: Date;
    const minValue = props.minValue;
    const maxValue = props.maxValue;

    // Check if the entire range fits within constraints
    const startFitsConstraints =
      !minValue || !maxValue || (start >= minValue && start <= maxValue);
    const endFitsConstraints =
      !minValue || !maxValue || (end >= minValue && end <= maxValue);
    const entireRangeFits = startFitsConstraints && endFitsConstraints;

    if (entireRangeFits) {
      // Perfect fit - no adjustment needed
      actualStart = start;
      actualEnd = end;
    } else {
      // Check if this is an overnight shift (end time is next day)
      const isOvernightShift =
        end.getDate() !== start.getDate() ||
        (end.getDate() === start.getDate() && end.getTime() < start.getTime());

      if (isOvernightShift) {
        // For overnight shifts, preserve the time pattern (e.g., 22:00-04:00)
        const startHour = start.getHours();
        const startMinute = start.getMinutes();
        const endHour = end.getHours();
        const endMinute = end.getMinutes();

        // Find the latest day where we can start the shift and still fit within constraints
        let foundValidDay = false;
        let daysBack = 0;

        while (daysBack < 365 && !foundValidDay) {
          const candidateStartDay = addDays(
            startOfDay(maxValue || new Date()),
            -daysBack,
          );
          const candidateStart = setHours(
            setMinutes(candidateStartDay, startMinute),
            startHour,
          );
          const candidateEnd = setHours(
            setMinutes(addDays(candidateStartDay, 1), endMinute),
            endHour,
          );

          // Check if this overnight shift fits within constraints
          const shiftFitsConstraints =
            (!minValue || candidateStart >= minValue) &&
            (!maxValue || candidateEnd <= maxValue);

          if (shiftFitsConstraints) {
            actualStart = candidateStart;
            actualEnd = candidateEnd;
            foundValidDay = true;
          }
          daysBack++;
        }

        if (!foundValidDay) {
          // Fallback: use the earliest possible overnight shift within constraints
          if (minValue) {
            const minDay = startOfDay(minValue);
            actualStart = setHours(setMinutes(minDay, startMinute), startHour);
            actualEnd = setHours(
              setMinutes(addDays(minDay, 1), endMinute),
              endHour,
            );

            // Ensure end doesn't exceed maxValue
            if (maxValue && actualEnd > maxValue) {
              actualEnd = maxValue;
            }
          } else {
            actualStart = start;
            actualEnd = end;
          }
        }
      } else {
        // For same-day shifts, use the previous logic
        const shiftDuration = end.getTime() - start.getTime();

        // Find the latest possible start time that allows the full duration to fit
        let latestPossibleStart = maxValue
          ? new Date(maxValue.getTime() - shiftDuration)
          : start;

        // Ensure start is not before minValue
        if (minValue && latestPossibleStart < minValue) {
          latestPossibleStart = minValue;
        }

        actualStart = latestPossibleStart;
        actualEnd = new Date(actualStart.getTime() + shiftDuration);

        // Final safety check - ensure end doesn't exceed maxValue
        if (maxValue && actualEnd > maxValue) {
          actualEnd = maxValue;
          actualStart = new Date(actualEnd.getTime() - shiftDuration);
        }
      }

      console.log('⚠️ Time range adjusted to fit within constraints:', {
        original: { start, end },
        adjusted: { actualStart, actualEnd },
        constraints: { minValue, maxValue },
        isOvernightShift,
        preservedPattern: isOvernightShift
          ? `${start.getHours()}:${String(start.getMinutes()).padStart(2, '0')}-${end.getHours()}:${String(end.getMinutes()).padStart(2, '0')}`
          : 'duration preserved',
      });
    }

    setTimeout(() => {
      props.onChange([
        toDateFromLocaleStringDate(actualStart),
        toDateFromLocaleStringDate(actualEnd),
      ]);
    }, 0);
  }

  // New: Function to get shift type-based durations (memoized to prevent re-renders)
  const shiftTypeBasedDurations = useMemo((): CustomDuration[] => {
    if (
      !props.shiftConfig?.enableShiftTypeSelectors ||
      !props.shiftConfig?.shiftTypes ||
      props.shiftConfig.shiftTypes.length === 0
    ) {
      return [];
    }

    const { shiftTypes } = props.shiftConfig;
    const typeDurations: CustomDuration[] = [];
    const maxValue = props.maxValue || new Date();
    const minValue = props.minValue || addDays(maxValue, -30);

    // Create selectors for ALL configured shift types
    _.forEach(shiftTypes, shiftType => {
      // Format time range for display with minutes
      const formatShiftTime = (hour: number, minute: number = 0) => {
        const displayHour = hour > 23 ? hour - 24 : hour;
        return `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      };

      const startTime = formatShiftTime(
        shiftType.startHour,
        shiftType.startMinute,
      );
      const endTime = formatShiftTime(shiftType.endHour, shiftType.endMinute);
      const timeRange = `${startTime}-${endTime}`;

      // Find the last occurrence of this shift type within data constraints
      let shiftStart: Date, shiftEnd: Date;
      let foundValidShift = false;

      // Start from the latest possible end time and work backwards
      const maxValue = props.maxValue || new Date();
      const minValue = props.minValue || addDays(maxValue, -30);

      // ---------------------------------------------------------------------
      // Enhanced overnight-shift support
      // Treat the shift as overnight if:
      //   • endHour ≥ 24  (explicit next-day notation, e.g. 28 → 04 next day)
      //   • endHour < startHour  (implicit next-day notation, e.g. 22 → 04)
      //   • endHour === startHour but endMinute < startMinute (rare, but still overnight)
      // ---------------------------------------------------------------------
      const startMin = shiftType.startHour * 60 + (shiftType.startMinute || 0);
      const rawEndHour = shiftType.endHour;
      const endHour24 = rawEndHour > 23 ? rawEndHour - 24 : rawEndHour; // normalised to 0-23
      const endMin = endHour24 * 60 + (shiftType.endMinute || 0);

      const isOvernightShiftType =
        rawEndHour > 23 ||
        rawEndHour < shiftType.startHour ||
        (rawEndHour === shiftType.startHour &&
          (shiftType.endMinute || 0) < (shiftType.startMinute || 0));

      // Calculate precise duration in milliseconds
      let actualShiftDurationMs: number;
      if (isOvernightShiftType) {
        const minutesUntilMidnight = 24 * 60 - startMin; // minutes from start to 00:00 next day
        actualShiftDurationMs = (minutesUntilMidnight + endMin) * 60 * 1000;
      } else {
        actualShiftDurationMs = (endMin - startMin) * 60 * 1000;
      }

      // Look backwards from maxValue to find the last shift that fits entirely within constraints
      for (let daysBack = 0; daysBack < 365 && !foundValidShift; daysBack++) {
        const candidateDate = addDays(startOfDay(maxValue), -daysBack);
        const candidateStart = setHours(
          setMinutes(candidateDate, shiftType.startMinute || 0),
          shiftType.startHour,
        );

        let candidateEnd: Date;
        if (isOvernightShiftType) {
          // Overnight shift – finishes on the following day
          candidateEnd = setHours(
            setMinutes(addDays(candidateDate, 1), shiftType.endMinute || 0),
            endHour24,
          );
        } else {
          candidateEnd = setHours(
            setMinutes(candidateDate, shiftType.endMinute || 0),
            endHour24,
          );
        }

        // Check if this entire shift fits within the available data range
        const shiftFitsInConstraints =
          candidateStart >= minValue && candidateEnd <= maxValue;

        if (shiftFitsInConstraints) {
          shiftStart = candidateStart;
          shiftEnd = candidateEnd;
          foundValidShift = true;
          break;
        }
      }

      if (!foundValidShift) {
        // Fallback: create the best possible shift at the end of available data range
        // Try to fit the shift as close to maxValue as possible while respecting constraints
        const idealEnd = maxValue;
        const idealStart = new Date(idealEnd.getTime() - actualShiftDurationMs);

        if (idealStart >= minValue) {
          // Perfect fit at the end
          shiftStart = idealStart;
          shiftEnd = idealEnd;
        } else {
          // Shift at the beginning of data range
          shiftStart = minValue;
          shiftEnd = new Date(shiftStart.getTime() + actualShiftDurationMs);

          // Ensure we don't exceed maxValue
          if (shiftEnd > maxValue) {
            shiftEnd = maxValue;
          }
        }
      }

      // Add the selector for this shift type
      const selectorTitle = t('Last {{shiftType}} ({{timeRange}})', {
        shiftType: shiftType.name,
        timeRange: timeRange,
      });

      typeDurations.push({
        title: selectorTitle,
        start: shiftStart,
        end: shiftEnd,
      });
    });

    return typeDurations;
  }, [
    props.shiftConfig?.enableShiftTypeSelectors,
    props.shiftConfig?.shiftTypes,
    props.maxValue,
    props.minValue,
    t,
  ]);

  // Function to get shift-based durations
  const getShiftBasedDurations = (): CustomDuration[] => {
    if (
      !props.shiftConfig?.enableShiftSelectors ||
      !props.shiftConfig?.shifts
    ) {
      return [];
    }

    const { shifts, maxShiftsToShow = 5 } = props.shiftConfig;

    // Sort shifts by end time, most recent first
    const sortedShifts = _.orderBy(shifts, ['end'], ['desc']);

    // Get the last N shifts
    const recentShifts = _.take(sortedShifts, maxShiftsToShow);

    const shiftDurations: CustomDuration[] = [];

    return shiftDurations;
  };

  // Memoize the custom durations array to prevent unnecessary re-renders
  const customDurations: CustomDuration[] = useMemo(
    () => [
      {
        title: t`Latest Activity (1h)`,
        start: addHours(props.maxValue, -1),
        end: props.maxValue,
      },
      // Add shift type-based durations first (most relevant)
      ...shiftTypeBasedDurations,
      // Add general shift-based durations
      ...getShiftBasedDurations(),
      // Standard time-based durations
      {
        title: t`Today`,
        start: startOfDay(props.maxValue),
        end: props.maxValue,
      },
      {
        title: t`Yesterday`,
        start: startOfDay(addDays(props.maxValue, -1)),
        end: endOfDay(addDays(props.maxValue, -1)),
      },
      {
        title: t`Last 7 Days`,
        start: startOfDay(addDays(props.maxValue, -7)),
        end: props.maxValue,
      },
      {
        title: t`Last 2 Weeks`,
        start: startOfDay(addWeeks(props.maxValue, -2)),
        end: props.maxValue,
      },
      {
        title: t`This Month`,
        start: startOfMonth(props.maxValue),
        end: props.maxValue,
      },
      {
        title: t`Last Month`,
        start: startOfMonth(addMonths(props.maxValue, -1)),
        end: startOfMonth(props.maxValue),
      },
    ],
    [props.maxValue, shiftTypeBasedDurations, t],
  );

  const selectedCustomDuration = _.find(
    customDurations,
    d => isEqual(d.start, props.value?.[0]) && isEqual(d.end, props.value?.[1]),
  );

  const timeMenuStyle = classNames(
    'px-3 py-1',
    'group-hover:bg-menu-active group-hover:text-menu-active-text group-hover:shadow-md',
    'group-hover:rounded-xl',
    // 'hover:bg-menu-active hover:text-menu-active-text hover:shadow-md',
    // 'hover:rounded-xl',
    'w-full',
    'flex-1',
  );

  return (
    <div
      data-component="DateRangePicker"
      className={classNames(
        'relative inline-flex flex-col',
        'text-start',
        'w-full flex-1',
        'text-xs xl:text-sm',
        props.className,
      )}
    >
      <span {...labelProps} className={classNames('text-menu-text text-sm')}>
        {_.isEmpty(props.label) ? '' : props.label}
      </span>
      <div
        {...groupProps}
        ref={ref}
        className={classNames(
          'group flex items-center',
          props.selectorPosition === 'start'
            ? 'mx-1 flex-row-reverse justify-end'
            : '',
        )}
      >
        <div
          className={classNames(
            props.headerMode ? 'h-6' : 'h-6',
            'w-auto', // panelHeaderMode // h-7
            'flex items-center',
            props.headerMode
              ? 'bg-app-header'
              : 'border-menu-active flex-1 border',
            props.selectorPosition === 'start'
              ? 'rounded-r-md'
              : 'rounded-l-md',
            'group-focus-within:border-menu group-focus-within:group-hover:border-menu',
            'transition-colors',
            'p-0.5 ltr:pr-2 rtl:pl-2',
            'active:bg-app-input',
            // 'lg:p-1',
            'relative',
            props.classNameInput,
          )}
        >
          {_.isNil(selectedCustomDuration) && (
            <>
              {state.validationState === 'invalid' && (
                <Icon.TriangleInfo
                  className={classNames('h-5 w-5', 'mx-0.5', 'fill-current')}
                />
              )}

              <InputDateField {...startFieldProps} />
              <span
                aria-hidden="true"
                className={classNames('px-0.5 lg:px-0.5', 'flex items-center')}
              >
                {'—'}
              </span>
              <InputDateField {...endFieldProps} />
            </>
          )}
          {!_.isNil(selectedCustomDuration) && (
            <Button {...buttonProps} className={classNames('px-2 py-1')}>
              {selectedCustomDuration.title}
            </Button>
          )}
        </div>
        <Button
          {...buttonProps}
          className={classNames(
            props.headerMode
              ? props.selectorPosition === 'start'
                ? 'h-6 rounded-l-md'
                : 'h-6 rounded-r-md'
              : props.selectorPosition === 'start'
                ? 'h-6 rounded-l-md'
                : 'h-6 rounded-r-md',
            'bg-menu-active text-menu-active-text',
            '!py-1',
            'group-focus-within:bg-app-panel-light/50 group-focus-within:text-menu-active',
          )}
        >
          <Icon.Calendar
            className={classNames(
              'fill-current',
              'h-4 w-4',
              'group-focus-within:text-menu',
            )}
          />
        </Button>
      </div>
      {state.isOpen && (
        <Popover triggerRef={ref} state={state} placement="bottom start">
          <Dialog {...dialogProps}>
            <div className={classNames('flex', 'text-menu-text', 'p-1')}>
              <ul
                data-component="TimeMenu"
                className={classNames(
                  'flex flex-col',
                  // 'divide-y divide-app-panel/50',
                  // 'space-y-4',
                  'text-xs',
                  'border-app-panel-dark/30 border-r',
                  'pl-4 pr-6',
                )}
              >
                {_.map(customDurations, d => (
                  <li
                    // className={timeMenuStyle}
                    className={classNames(
                      'group py-1',
                      'border-app-panel/70 border-b',
                      'cursor-pointer',
                      'w-full flex-1',
                    )}
                    key={d.title}
                    onClick={() => selectDuration(d.start, d.end)}
                  >
                    <div className={timeMenuStyle}>{d.title}</div>
                  </li>
                ))}
              </ul>
              <div className={classNames('pl-6 pr-4')}>
                <RangeCalendar
                  {...calendarProps}
                  onResetClick={props.onResetClick}
                />
              </div>
            </div>
          </Dialog>
        </Popover>
      )}
    </div>
  );
};

export default DateRangePicker;

// Export types for external usage
export type { ShiftPeriod, ShiftSelectorConfig, ShiftTypeConfig };

// Export default shift types
export { getDefaultShiftTypes };

// Export the categorization function
export { categorizeShiftByTime };

// Helper function to convert various shift data formats to ShiftPeriod
export const createShiftSelectorConfig = (params: {
  shifts?: {
    shiftStart?: Date;
    shiftEnd?: Date;
    startTime?: Date;
    endTime?: Date;
    from?: Date;
    to?: Date;
    shiftId?: string;
    employeeId?: string;
    id?: string;
  }[];
  maxShiftsToShow?: number;
  enableShiftSelectors?: boolean;
  enableShiftTypeSelectors?: boolean;
  shiftTypes?: ShiftTypeConfig[];
}): ShiftSelectorConfig => {
  const {
    shifts = [],
    maxShiftsToShow = 5,
    enableShiftSelectors = true,
    enableShiftTypeSelectors = false,
    shiftTypes = [],
  } = params;

  const normalizedShifts: ShiftPeriod[] = shifts
    .map((shift, index) => {
      // Handle different shift data formats
      const start = shift.shiftStart || shift.startTime || shift.from;
      const end = shift.shiftEnd || shift.endTime || shift.to;

      if (!start || !end) {
        return null;
      }

      return {
        start,
        end,
        shiftId: shift.shiftId || shift.id,
        employeeId: shift.employeeId,
      } as ShiftPeriod;
    })
    .filter((shift): shift is ShiftPeriod => shift !== null);

  return {
    shifts: normalizedShifts,
    maxShiftsToShow,
    enableShiftSelectors,
    enableShiftTypeSelectors,
    shiftTypes,
  };
};
